import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PostCard from '@/components/base/PostCard';
import useSeo from '@/hooks/useSeo';
import { categories, posts } from '@/mocks/posts';

const sortTabs = [
  { key: 'latest', label: '最新更新' },
  { key: 'hot', label: '热度最高' },
  { key: 'views', label: '围观最多' },
];

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const [sort, setSort] = useState('latest');

  const list = useMemo(() => {
    const base = posts.filter((p) => p.categorySlug === slug);
    if (sort === 'hot') return [...base].sort((a, b) => b.heat - a.heat);
    if (sort === 'views') return [...base].sort((a, b) => b.views - a.views);
    return [...base].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  }, [slug, sort]);

  const label = category?.label ?? '栏目';

  useSeo({
    title: category
      ? `${label}栏目 | 18爆料网最新猛料合集`
      : '栏目未找到 | 18爆料网',
    description: category
      ? `18爆料网${label}栏目，聚合最新${label}爆料与热门内容，每日更新，帮你把事件前因后果一次看清。`
      : '你访问的栏目不存在，返回 18爆料网首页查看今日爆料。',
    keywords: `18爆料网,${label},18爆料网今日爆料,18爆料网热门爆料`,
    path: `/category/${slug}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${label} - 18爆料网`,
        inLanguage: 'zh-CN',
        description: category?.desc ?? '',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: label },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: list.map((post, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: post.title,
          url: `/post/${post.id}`,
        })),
      },
    ],
  });

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex w-full flex-col items-center justify-center px-4 py-24 text-center">
          <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-emotion-sad-line" aria-hidden="true"></i>
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">没找到这个栏目</h1>
          <p className="mt-3 text-sm text-foreground-600">栏目可能已调整，回首页看看今天的爆料吧。</p>
          <Link
            to="/"
            className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="w-full">
        <Breadcrumb items={[{ label: category.label }]} />

        <header className="w-full px-4 pb-8 md:px-8">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-9">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className={`${category.icon} text-2xl`} aria-hidden="true"></i>
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              {category.label}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {category.desc}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-article-line" aria-hidden="true"></i>
                共 {list.length} 篇
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-refresh-line" aria-hidden="true"></i>
                每日更新
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-shield-check-line" aria-hidden="true"></i>
                含时间线核对
              </span>
            </div>
          </div>
        </header>

        <div className="w-full px-4 pb-12 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
            <div>
              <div className="mb-5 flex w-full items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1 sm:w-auto">
                {sortTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setSort(tab.key)}
                    className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors sm:flex-none ${
                      sort === tab.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {list.length > 0 ? (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {list.map((post) => (
                    <PostCard key={post.id} post={post} variant="grid" />
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-background-200 bg-background-50 p-8 text-center text-sm text-foreground-500">
                  这个栏目还在整理中，稍后再来看看。
                </p>
              )}
            </div>

            <aside className="flex flex-col gap-6" aria-label="侧边栏">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
                  全站热榜
                </h2>
                <div className="divide-y divide-background-200">
                  {[...posts]
                    .sort((a, b) => b.heat - a.heat)
                    .slice(0, 5)
                    .map((post, index) => (
                      <PostCard key={post.id} post={post} variant="compact" rank={index + 1} />
                    ))}
                </div>
              </div>

              <div className="rounded-lg bg-foreground-950 p-5">
                <h2 className="font-heading text-lg font-bold text-background-50">有料要爆？</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-300">
                  你手里的截图、录音、聊天记录，可能就是一个大事件的起点。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line" aria-hidden="true"></i>
                  去投稿
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">其它栏目</h2>
                <ul className="space-y-2 text-sm">
                  {categories
                    .filter((c) => c.slug !== slug)
                    .map((c) => (
                      <li key={c.slug}>
                        <Link
                          to={`/category/${c.slug}`}
                          className="flex items-center justify-between rounded-md px-3 py-2 text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                        >
                          {c.label}
                          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}